/**
 * @file The My team goal of a caller in several teams, from one goal read per
 * team: the found targets added exactly through `analyticsDecimalSum`.
 * Consumers: the Overview panel and `useHomeGoalProgress`. Any loading read
 * keeps the whole read loading, then a denied or failed read passes through; a
 * team with no goal counts zero, and no found goal at all reads not found. A
 * single found read, or a found target that is no decimal, is returned as it
 * is, so `analyticsGoalReadProgress` still fails it with its own message. Team
 * goals share one fiscal year, so the sum keeps the first goal's period.
 */
import { analyticsDecimalFraction, analyticsDecimalSum } from '#types';

import type { AnalyticsGoalRead } from './analyticsOverviewTab.types';

type FoundRead = Extract<AnalyticsGoalRead, { status: 'found' }>;

export const analyticsTeamGoalRead = (
  reads: readonly AnalyticsGoalRead[],
): AnalyticsGoalRead => {
  const loading = reads.find((read) => read.status === 'loading');
  if (loading) return loading;
  const unread = reads.find(
    (read) => read.status === 'denied' || read.status === 'failed',
  );
  if (unread) return unread;
  const found = reads.filter((read): read is FoundRead => read.status === 'found');
  const [first] = found;
  if (first === undefined) return { status: 'notFound' };
  const undecimal = found.find(
    (read) => analyticsDecimalFraction(read.goal.target) === undefined,
  );
  if (undecimal) return undecimal;
  if (found.length === 1) return first;
  return {
    status: 'found',
    goal: {
      target: analyticsDecimalSum(found.map((read) => read.goal.target)),
      periodStart: first.goal.periodStart,
      periodEnd: first.goal.periodEnd,
    },
  };
};
